import {
  Bars3Icon,
  BuildingStorefrontIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  CubeIcon,
  FaceSmileIcon,
  FolderIcon,
  GiftIcon,
  PresentationChartBarIcon,
  ShoppingBagIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { FeatureFlag, ServerFeatureFlag } from '@/components/feature-flag';
import { FeatureFlagType } from '@/components/feature-flag/types';
import { ServerMenuVisibility } from '@/components/menu-visibility';
import { MobileMenu, useRecordRecentMenuPage } from '@/components/mobile-menu';
import { HeaderNoticeMenu } from '@/features/notices/header-notice-menu';
import { HeaderMenuIcon } from '@/features/settings/header-menus/header-menu-icons';
import { toHeaderMenuIconValue } from '@/features/settings/header-menus/header-menu-types';
import { useAuthCheck } from '@/hooks/auth';
import { useDefaultMenuPath } from '@/hooks/use-default-menu-path';
import logo from '../assets/logo.png';

const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
  clsx(
    'flex flex-col items-center text-[14px] w-14 py-[3px] duration-300 opacity-50 text-white',
    isActive ? 'bg-white/20 opacity-100' : 'hover:bg-white/20 hover:opacity-100',
  );

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data } = useAuthCheck();
  const location = useLocation();
  const defaultMenuPath = useDefaultMenuPath();
  useRecordRecentMenuPage();
  const customMenus = data.user?.organization.header_menus ?? [];
  const isSettingsActive = location.pathname.startsWith('/settings');
  const isPurchaseOrdersActive = location.pathname.startsWith('/purchase-orders');
  const isManufacturingOrdersActive = location.pathname.startsWith('/manufacturing-orders');

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <header className={clsx('bg-zinc-800', isMenuOpen && 'active')}>
      <div>
        {data.isAuthenticated && data.user && (
          <div className="flex items-center justify-end gap-2 border-b border-neutral-50/20 text-white/70 text-xs px-2 py-0.5">
            <HeaderNoticeMenu />
            <span className="text-white/40">|</span>
            <span>{data.user.organization.name}</span>
            <span className="text-white/40">|</span>
            <span>{data.user.name}</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-5 w-full justify-between h-14 px-5">
        <div className="flex items-center gap-4">
          <h1 className="relative z-50">
            <Link to={defaultMenuPath}>
              <img src={logo} alt="アニマルオフィス" className="h-10 w-auto" />
            </Link>
          </h1>
        </div>
        <nav className="hidden md:flex justify-between flex-1">
          <div className="flex gap-4 flex-1">
            <FeatureFlag flag={FeatureFlagType.Demo}>
              <NavLink to="/chats" className={navLinkClasses}>
                <ChatBubbleLeftRightIcon className="w-6" />
                会話
              </NavLink>
            </FeatureFlag>
            <ServerFeatureFlag featureType="CRM">
              <NavLink to="/leads" className={navLinkClasses}>
                <FaceSmileIcon className="w-6" />
                見込客
              </NavLink>
            </ServerFeatureFlag>
            <ServerMenuVisibility menuType="PROJECTS">
              <NavLink to="/projects" className={navLinkClasses}>
                <FolderIcon className="w-6" />
                案件
              </NavLink>
            </ServerMenuVisibility>
            <ServerMenuVisibility menuType="ORDERS">
              <NavLink to="/orders" className={navLinkClasses}>
                <BuildingStorefrontIcon className="w-6" />
                受注
              </NavLink>
            </ServerMenuVisibility>
            <ServerMenuVisibility menuType="PURCHASE_ORDERS">
              <NavLink
                to="/purchase-orders"
                className={() => navLinkClasses({ isActive: isPurchaseOrdersActive })}
              >
                <ShoppingBagIcon className="w-6" />
                発注
              </NavLink>
            </ServerMenuVisibility>
            <ServerMenuVisibility menuType="INVENTORY">
              <NavLink to="/inventory" className={navLinkClasses}>
                <CubeIcon className="w-6" />
                在庫
              </NavLink>
            </ServerMenuVisibility>
            <ServerMenuVisibility menuType="MANUFACTURING">
              <NavLink
                to="/manufacturing-orders"
                className={() => navLinkClasses({ isActive: isManufacturingOrdersActive })}
              >
                <WrenchScrewdriverIcon className="w-6" />
                生産
              </NavLink>
            </ServerMenuVisibility>
            <ServerMenuVisibility menuType="PRODUCTS">
              <NavLink to="/products" className={navLinkClasses}>
                <GiftIcon className="w-6" />
                商品
              </NavLink>
            </ServerMenuVisibility>
            <ServerMenuVisibility menuType="CUSTOMERS">
              <NavLink to="/customers" className={navLinkClasses}>
                <UserGroupIcon className="w-6" />
                取引先
              </NavLink>
            </ServerMenuVisibility>
            <ServerMenuVisibility menuType="EXPLORE">
              <NavLink to="/explore" className={navLinkClasses}>
                <PresentationChartBarIcon className="w-6" />
                探索
              </NavLink>
            </ServerMenuVisibility>
            <FeatureFlag flag={FeatureFlagType.Demo}>
              <NavLink to="/insights" className={navLinkClasses}>
                <PresentationChartBarIcon className="w-6" />
                探索
              </NavLink>
            </FeatureFlag>
            {customMenus.map((menu) =>
              menu.url.startsWith('/') ? (
                <NavLink key={menu.position} to={menu.url} className={navLinkClasses}>
                  <HeaderMenuIcon icon={toHeaderMenuIconValue(menu)} />
                  {menu.label}
                </NavLink>
              ) : (
                <a
                  key={menu.position}
                  href={menu.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={navLinkClasses({ isActive: false })}
                >
                  <HeaderMenuIcon icon={toHeaderMenuIconValue(menu)} />
                  {menu.label}
                </a>
              ),
            )}
          </div>
          <div>
            <NavLink
              to="/settings/organization"
              className={() => navLinkClasses({ isActive: isSettingsActive })}
            >
              <Cog6ToothIcon className="w-6" />
              設定
            </NavLink>
          </div>
        </nav>
        {isMenuOpen && <MobileMenu onClose={closeMenu} />}
        <button
          type="button"
          className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 transition duration-300 z-50 relative"
          onClick={toggleMenu}
        >
          <Bars3Icon
            className={clsx(
              'w-6 text-white absolute transition-all duration-300',
              isMenuOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0',
            )}
          />
          <XMarkIcon
            className={clsx(
              'w-6 text-white absolute transition-all duration-300',
              isMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90',
            )}
          />
        </button>
      </div>
    </header>
  );
};
